import { InstructorSlideDeckCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSlideDeckCubit'
import { type SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import type { SlideQuestion } from '@breakoutlearning/firebase-repository/models/SlideQuestion'
import {
  InstitutionIntegrationType,
  SlideDeckMaterialWorkType,
  SlideDeckReferenceType,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import {
  BreakoutAsyncButton,
  BreakoutButton,
} from 'components/design-system/BreakoutButton'
import { GoogleStorageImage } from 'components/GoogleStorageImage'
import { ChevronLeft } from 'components/icons/ChevronLeft'
import { ClockIcon } from 'components/icons/Clock'
import { DownloadIcon } from 'components/icons/Download'
import { EyeOpen } from 'components/icons/EyeOpen'
import { LinkIcon } from 'components/icons/Link'
import { Money } from 'components/icons/Money'
import { PlusIcon } from 'components/icons/Plus'
import { CourseMaterialButton } from 'components/slide-deck-material-components/CourseMaterialListItem'
import { Spinner } from 'components/Spinner'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import {
  InstructorSlideDeckCubitProvider,
  useInstructorSlideDeckCubit,
} from 'hooks/cubits/instructorSlideDeck'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser, useImpersonatedBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useLTIMode } from 'hooks/use-lti-mode'
import { observer } from 'mobx-react-lite'
import type { HeadlineBreadcrumb } from 'pages/layout/TwoColumn'
import {
  Actions,
  Contents,
  Header,
  Headline,
  MainPane,
} from 'pages/layout/TwoColumn'
import { ShowExperienceDialog } from 'pages/student/assignment/dialogs/ShowExperienceDialog'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { CreateOrEditSectionDialog } from '../classes/CreateOrEditSectionDialog'
import { RevampedSlideDeckTrailerThumbnail } from '../shared/RevampedSlideDeckCard'
import { useFeatureFlags } from 'hooks/featureFlags'
import { CreateAssignmentDialogGrouping } from './AssignmentDialog/CreateAssignmentDialog'
import { LtiCreateAssignmentDialog } from './LtiAssignDialog/LtiCreateAssignmentDialog'
import { PageTitle } from 'components/PageTitle'
import { InternalLink } from 'components/core/InternalLink'
import { useTranslationTyped } from 'i18n/i18n'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '@breakoutlearning/firebase-repository/constants'
import { SlideDeckDescriptionMarkdown } from 'components/SlideDeckDescriptionMarkdown'
import { isSignedUrl } from 'util/storageUrl'

export const InstructorSlideDeckPage = observer(
  function InstructorSlideDeckPage({ slideDeckId }: { slideDeckId: string }) {
    const { t } = useTranslation()
    const rootStore = useRootStore()
    const user = useBreakoutUser()
    const instructorUserId = rootStore.impersonationInfo?.userId
    const queryParams = rootStore.router.queryParams
    const repository = useRepository()
    const paramCatalogId = rootStore.router.queryParams?.catalogId
    const sectionId = queryParams?.sectionId?.toString()
    const catalogId =
      typeof paramCatalogId === 'string' ? paramCatalogId : undefined
    const paramNextbookId =
      rootStore.router.queryParams?.nextbookId ||
      rootStore.router.queryParams?.teachingPlanId
    const teachingPlanId =
      typeof paramNextbookId === 'string' ? paramNextbookId : undefined
    const impersonatedBreakoutUser = useImpersonatedBreakoutUser()

    const cubit = useCubitBuilder(
      () =>
        new InstructorSlideDeckCubit(
          repository,
          slideDeckId,
          impersonatedBreakoutUser,
          {
            instructorUserId,
            catalogId,
            teachingPlanId,
            sectionId,
          }
        ),
      [
        repository,
        slideDeckId,
        catalogId,
        teachingPlanId,
        impersonatedBreakoutUser,
      ]
    )
    const { showDialog } = useDialogs()
    const { ltiMode } = useLTIMode()

    const slideDeckName = cubit.slideDeck.data.slideDeckName
    const teachingPlanName = cubit.teachingPlan.data.teachingPlanName
    const sectionName = cubit.section.sectionLabelWithClass

    const breadcrumbs = useMemo(() => {
      const crumbs: HeadlineBreadcrumb[] = []

      if (sectionId) {
        crumbs.push({
          label: t('instructor_classes.headline'),
          onClick: () => {
            rootStore.navigateToInstructorClassesPage()
          },
        })
        crumbs.push({
          label: sectionName,
          onClick: () => {
            rootStore.navigateToInstructorClassPage(sectionId)
          },
        })
      }

      crumbs.push({
        label: t('instructor_library.breadcrumb'),
        onClick: () => {
          rootStore.navigateToInstructorCatalogPage({
            ...queryParams,
            sectionId: queryParams?.sectionId,
            catalogId: queryParams?.catalogId,
          })
        },
      })

      if (catalogId && teachingPlanId) {
        crumbs.push({
          label: teachingPlanName,
          onClick: () => {
            rootStore.navigateToTeachingPlanPage(catalogId, teachingPlanId, {
              ...queryParams,
              sectionId: queryParams?.sectionId,
            })
          },
        })
      }

      crumbs.push({
        label: slideDeckName,
      })

      return crumbs
    }, [
      sectionId,
      t,
      catalogId,
      teachingPlanId,
      slideDeckName,
      sectionName,
      rootStore,
      queryParams,
      teachingPlanName,
    ])

    const canAddAssignment =
      !ltiMode && !cubit.section.isLtiCourse && !user.isLtiUser

    return (
      <MainPane>
        <PageTitle title={slideDeckName} />
        <Header>
          <Headline
            breadcrumbs={breadcrumbs}
            label={cubit.slideDeck.data.slideDeckName}
          />
          <Actions className="flex-nowrap gap-5! pl-4">
            {ltiMode && (
              <BreakoutButton
                data-testid="add-to-class-button"
                kind="accent"
                size="large"
                icon={<PlusIcon size={14} />}
                onClick={() => {
                  showDialog(() => {
                    return <LtiCreateAssignmentDialog cubit={cubit} />
                  })
                  return
                }}
              >
                {ltiMode === InstitutionIntegrationType.d2l &&
                  t('instructor_library.add_to_brightspace')}
                {ltiMode === InstitutionIntegrationType.blackboard &&
                  t('instructor_library.add_to_blackboard')}
                {ltiMode === InstitutionIntegrationType.canvas &&
                  t('instructor_library.add_to_canvas')}
              </BreakoutButton>
            )}
            <BreakoutButton
              size="large"
              kind="secondary"
              icon={<EyeOpen size={14} />}
              onClick={() =>
                showDialog(() => (
                  <ShowExperienceDialog slideDeckId={slideDeckId} />
                ))
              }
            >
              {t('instructor_library.preview_experience')}
            </BreakoutButton>
            {/*
                only show the assign class dialog if catalogId is set and the deck is not archived
                also, wait for sections to load before showing the dialog so that we know which dialog
                to show
             */}
            {canAddAssignment &&
              !!queryParams?.catalogId &&
              !cubit.slideDeck.isAutomaticallyArchived &&
              cubit.sections.isLoaded && (
                <BreakoutButton
                  data-testid="add-to-class-button"
                  kind="accent"
                  size="large"
                  onClick={() => {
                    const showAssignClassDialog = () => {
                      showDialog(({ remove }) => {
                        return (
                          <CreateAssignmentDialogGrouping
                            cubit={cubit}
                            onCreate={remove}
                          />
                        )
                      })
                    }
                    // For TA's, we never show add to class dialog
                    if (user.isTA) {
                      showAssignClassDialog()
                      return
                    }
                    if (
                      !(cubit.sections.value && cubit.sections.value.length > 0)
                    ) {
                      return showDialog(({ remove }) => (
                        <CreateOrEditSectionDialog
                          editOrCreate="create"
                          createSectionInvoiceRequest={
                            cubit.createSectionInvoiceRequest
                          }
                          saveSection={cubit.createSection}
                          onSave={({ sectionId }) => {
                            if (!sectionId) return

                            rootStore.updateQueryParams({ sectionId })

                            remove()
                            showAssignClassDialog()
                          }}
                          redeemPromotions={cubit.redeemPromotions}
                          applyApprovedFreeAdminPromotion={
                            cubit.applyApprovedFreeAdminPromotion
                          }
                        />
                      ))
                    }

                    showAssignClassDialog()
                  }}
                >
                  {t('instructor_library.add_to_class')}
                </BreakoutButton>
              )}
          </Actions>
        </Header>

        <Contents className="h-full min-h-0 md:overflow-clip">
          <InstructorSlideDeckCubitProvider value={cubit}>
            <SlideDeckContent />
          </InstructorSlideDeckCubitProvider>
        </Contents>
      </MainPane>
    )
  }
)

export const SlideDeckContent = observer(function SlideDeckContent({
  showPreviewBtn = false,
}: {
  showPreviewBtn?: boolean
}) {
  const cubit = useInstructorSlideDeckCubit()
  const { t, tt } = useTranslationTyped()
  const { showDialog } = useDialogs()
  const { isFaculty, isInternal } = useBreakoutUser()
  const { currentUser } = useRepository()
  const { hideSlideDeckPrice } = useFeatureFlags()

  const materials = cubit.materials.models
  const slideDeck = cubit.slideDeck

  const instructorMaterials = useMemo(() => {
    return materials.filter(
      (material) =>
        material.data.viewableByInstructor && !material.data.viewableByStudent
    )
  }, [materials])

  const preWorkMaterials = useMemo(() => {
    return materials.filter(
      (material) =>
        material.data.viewableByStudent &&
        material.data.materialWorkType === SlideDeckMaterialWorkType.preWork
    )
  }, [materials])

  const otherMaterials = useMemo(() => {
    return materials.filter(
      (material) =>
        material.data.viewableByStudent &&
        material.data.materialWorkType !== SlideDeckMaterialWorkType.preWork
    )
  }, [materials])

  const preQuizQuestions = useMemo(() => {
    return cubit.questionsSorted.filter(
      (question) => question.data.slideId === PRE_MEETING_QUESTION_SLIDE_ID
    )
  }, [cubit.questionsSorted])

  if (
    !cubit.materials.isLoaded ||
    !cubit.slideDeck.isLoaded ||
    !cubit.authors.isLoaded
  ) {
    return (
      <div className="mt-20 flex w-full justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="h-full overflow-clip">
      <div className={classNames('h-full overflow-y-auto pr-1')}>
        <div>
          <RevampedSlideDeckTrailerThumbnail
            slideDeckMaterials={materials}
            squareThumbnail={false}
            hideEmptyThumbnail={true}
            canPlayVideoThumbnails={true}
          />
        </div>
        <div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-center">
              {slideDeck.data.slideDeckImageURL && (
                <img
                  src={slideDeck.data.slideDeckImageURL}
                  className="max-h-[65px] max-w-[65px] p-1"
                  alt=""
                />
              )}
              <div className="ml-2">
                <h1 className="text-headline-large text-on-surface">
                  {slideDeck.data.slideDeckName}
                </h1>
                <p className="text-label-large text-on-surface-var">
                  {slideDeck.data.slideDeckTeaser}
                </p>
              </div>
            </div>
            <div className="mr-2 flex flex-row-reverse flex-wrap items-center gap-2">
              {showPreviewBtn && (
                <BreakoutButton
                  size="large"
                  kind="secondary"
                  className="mr-1"
                  icon={<EyeOpen size={15} />}
                  onClick={() => {
                    showDialog(() => (
                      <ShowExperienceDialog slideDeckId={cubit.slideDeckId} />
                    ))
                  }}
                >
                  {t('instructor_library.preview_experience')}
                </BreakoutButton>
              )}
              {!hideSlideDeckPrice && (
                <div className="flex h-[52px] items-center justify-between gap-2 rounded-xl border border-outline-light px-4 text-title-medium">
                  <Money size={12} />
                  <Trans
                    i18nKey={
                      slideDeck.priceInDollars === '0'
                        ? 'instructor_library.price_zero'
                        : 'instructor_library.price'
                    }
                    values={{ price: slideDeck.priceInDollars }}
                    components={{
                      light: (
                        <div
                          className={classNames(
                            'text-body-medium',
                            slideDeck.priceInDollars === '0'
                              ? 'text-on-surface'
                              : 'text-on-surface-var'
                          )}
                        />
                      ),
                    }}
                  />
                </div>
              )}
              {cubit.estimatedExperienceDurationInMinutes > 0 && (
                <div className="flex h-[52px] items-center justify-between gap-2 rounded-xl border border-outline-light px-4">
                  <div>
                    <ClockIcon size={15} />
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="text-title-medium">
                      {cubit.estimatedExperienceDurationInMinutes}
                    </div>
                    <div className="text-body-medium text-on-surface-var">
                      {t('instructor_library.minutes')}
                    </div>
                  </div>
                </div>
              )}
              <AdminOnlyRing
                onlyShowIf={isInternal}
                label={tt.roles.internal_only()}
              >
                <div className="text-body-large font-light">
                  <InternalLink
                    to="adminSlideDeck"
                    params={{ slideDeckId: slideDeck.id }}
                    className="flex items-center gap-2 text-on-surface hover:underline"
                  >
                    <strong className="text-title-medium">
                      {tt.library.slide_deck_version_label()}:{' '}
                    </strong>
                    <strong>{slideDeck.data.slideDeckVersion}</strong>
                  </InternalLink>
                </div>
              </AdminOnlyRing>
            </div>
          </div>

          {cubit.references.models.length > 0 &&
            cubit.references.models.map((reference) => {
              const referencePresentation = getReferencePresentation(
                reference.data.referenceType,
                tt
              )

              return (
                <div
                  key={reference.id}
                  className="mt-3 flex items-center rounded-2xl bg-surface px-4 py-3"
                >
                  <div
                    className={classNames(
                      'flex items-center divide-x-2',
                      referencePresentation.dividerClassName
                    )}
                  >
                    <h2 className="mr-3 w-[100px] text-label-large text-on-surface">
                      {referencePresentation.label}
                    </h2>
                    <div className="pl-3">
                      <p className="text-title-medium text-on-surface italic">
                        {reference.data.referenceTitle}
                      </p>
                      <p className="text-body-large text-on-surface-var">
                        {reference.data.referenceAuthor},{' '}
                        {reference.data.referencePublisher},{' '}
                        {reference.getFormattedPublishedAt()}
                      </p>
                    </div>
                  </div>
                  <div className="flex grow items-center justify-end">
                    <LinkIcon
                      size={16}
                      className="cursor-pointer"
                      onClick={() => {
                        window.open(reference.data.referenceURL, '_blank')
                      }}
                    />
                  </div>
                </div>
              )
            })}

          <div className="mt-3 text-body-large text-on-surface-var">
            <SlideDeckDescriptionMarkdown>
              {slideDeck.data.slideDeckDescription || ''}
            </SlideDeckDescriptionMarkdown>
          </div>

          {cubit.authors.models.length > 0 && (
            <div className="mt-3">
              <div className="text-title-medium text-on-surface">
                {tt.library.generated_by()}
              </div>
              <div className="flex flex-col gap-y-3">
                {cubit.authors.models.map((author) => (
                  <div
                    className="flex flex-row items-center gap-2 first:mt-1"
                    key={author.id}
                  >
                    {author.data.authorImageURL && (
                      <div>
                        <GoogleStorageImage
                          src={author.data.authorImageURL}
                          className="aspect-square max-h-[30px] max-w-[30px] rounded-full border-1 border-surface"
                          namespace="instructor-library"
                          alt=""
                        />
                      </div>
                    )}
                    <div>
                      <div className="line-clamp-1 text-title-medium text-on-surface">
                        {author.data.authorName}
                      </div>
                      <div className="line-clamp-1 text-body-medium text-on-surface-var">
                        {author.data.authorTitle}—
                        {author.data.authorInstitution}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cubit.slideDeck.data.slideDeckLearningObjectives.length > 0 && (
            <div className="mt-3">
              <h2 className="text-title-large text-on-surface">
                {t('instructor_library.learning_objectives')}
              </h2>
              <ul className="list-disc pl-5 marker:text-on-surface-var">
                {cubit.slideDeck.data.slideDeckLearningObjectives.map(
                  (objective, index) => (
                    <li
                      key={objective}
                      className={classNames(index !== 0 && '-mt-1.5')}
                    >
                      <div className="mt-1 flex flex-row items-center gap-2">
                        <div className="text-body-large text-on-surface-var">
                          {objective}
                        </div>
                      </div>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}

          {cubit.rubrics.length > 0 && (
            <div className="mt-3">
              <h2 className="text-title-large text-on-surface">
                {t('instructor_library.rubrics')}
              </h2>
              <ul className="list-disc pl-5 marker:text-on-surface-var">
                {cubit.sortedRubrics.map((rubric, index) => (
                  <li
                    key={rubric.id}
                    className={classNames(index !== 0 && '-mt-1.5')}
                  >
                    <div className="mt-1 flex flex-col gap-0.5">
                      <div className="text-label-large text-on-surface">
                        {rubric.data.rubric}
                      </div>
                      <div className="text-body-large text-on-surface-var">
                        <RoleLimitLabel
                          assignmentRoles={rubric.data.assignmentRoles}
                          className="text-body-large font-bold"
                          suffix=". "
                        />
                        {rubric.data.rubricDescription}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {instructorMaterials.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-row items-center justify-between">
                <h2 className="text-title-large text-on-surface">
                  {t('instructor_library.instructor_materials')}
                </h2>
                {!preWorkMaterials.length &&
                  !otherMaterials.length &&
                  (isInternal || isFaculty) && (
                    <BreakoutAsyncButton
                      onClick={async () =>
                        await downloadMaterials(
                          cubit.slideDeck.safeFileName(),
                          materials,
                          currentUser!.getIdToken()
                        )
                      }
                      size="small"
                      kind="secondary"
                      icon={<DownloadIcon size={14} />}
                      loadingText={t('instructor_library.download_materials')}
                    >
                      {t('instructor_library.download_materials')}
                    </BreakoutAsyncButton>
                  )}
              </div>
              <div className="mt-2 mr-1 grid grid-cols-2 gap-x-3 gap-y-2">
                {instructorMaterials.map((material) => (
                  <div
                    key={material.id}
                    className="flex h-[52px] items-center justify-between rounded-2xl bg-surface px-4"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <p className="line-clamp-1 text-label-medium text-on-surface">
                        {material.data.materialName}
                      </p>
                      <RoleLimitLabel
                        assignmentRoles={material.data.assignmentRoles}
                      />
                    </div>
                    <CourseMaterialButton
                      material={material}
                      slideDeckId={cubit.slideDeckId}
                      slideDeckName={cubit.slideDeck.data.slideDeckName}
                      slideDeckVersion={cubit.slideDeck.data.slideDeckVersion}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="relative flex items-center py-4">
            <div className="grow border-t-1 border-fixed-accent-color"></div>
            <strong className="mx-4 shrink text-title-medium text-on-surface">
              {t('instructor_library.student_experience')}
            </strong>
            <div className="grow border-t-1 border-fixed-accent-color"></div>
          </div>

          {(preWorkMaterials.length > 0 || otherMaterials.length > 0) && (
            <div className="rounded-[20px] border border-outline-light px-8 py-5">
              <div className="flex flex-row justify-between">
                <h1 className="text-headline-medium text-on-surface">
                  {t('instructor_library.materials')}
                </h1>
                {(isInternal || isFaculty) && (
                  <BreakoutAsyncButton
                    onClick={async () =>
                      await downloadMaterials(
                        cubit.slideDeck.safeFileName(),
                        materials,
                        currentUser!.getIdToken()
                      )
                    }
                    size="small"
                    kind="secondary"
                    icon={<DownloadIcon size={14} />}
                    loadingText={t('instructor_library.download_materials')}
                  >
                    {t('instructor_library.download_materials')}
                  </BreakoutAsyncButton>
                )}
              </div>
              <div
                className={classNames(
                  'mt-3 grid gap-5',
                  preWorkMaterials.length === 0 || otherMaterials.length === 0
                    ? 'grid-cols-1'
                    : 'grid-cols-2'
                )}
              >
                {preWorkMaterials.length > 0 && (
                  <div>
                    <h2 className="text-title-medium text-on-surface">
                      {t('instructor_library.pre_work_materials')}
                    </h2>
                    <div className="mt-1 flex flex-col gap-y-2">
                      {preWorkMaterials.map((material) => (
                        <div
                          key={material.id}
                          className="flex h-[52px] items-center justify-between rounded-2xl bg-surface px-4"
                        >
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <p className="line-clamp-1 text-label-medium text-on-surface">
                              {material.data.materialName}
                            </p>
                            <RoleLimitLabel
                              assignmentRoles={material.data.assignmentRoles}
                            />
                          </div>
                          <CourseMaterialButton
                            material={material}
                            slideDeckVersion={
                              cubit.slideDeck.data.slideDeckVersion
                            }
                            slideDeckId={cubit.slideDeckId}
                            slideDeckName={cubit.slideDeck.data.slideDeckName}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {otherMaterials.length > 0 && (
                  <div>
                    {preWorkMaterials.length !== 0 && (
                      <h2 className="text-title-medium text-on-surface">
                        {t('instructor_library.other_materials')}
                      </h2>
                    )}

                    <div
                      className={classNames('mt-1 grid gap-2', {
                        'grid-cols-2': preWorkMaterials.length === 0,
                      })}
                    >
                      {otherMaterials.map((material) => (
                        <div
                          key={material.id}
                          className="flex h-[52px] items-center justify-between rounded-2xl bg-surface px-4"
                        >
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <p className="line-clamp-1 text-label-medium text-on-surface">
                              {material.data.materialName}
                            </p>
                            <RoleLimitLabel
                              assignmentRoles={material.data.assignmentRoles}
                            />
                          </div>
                          <CourseMaterialButton
                            material={material}
                            slideDeckId={cubit.slideDeckId}
                            slideDeckName={cubit.slideDeck.data.slideDeckName}
                            slideDeckVersion={
                              cubit.slideDeck.data.slideDeckVersion
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {preQuizQuestions.length > 0 && (
            <div className="mt-3 rounded-[20px] border border-outline-light px-8 py-5">
              <h1 className="text-headline-medium text-on-surface">
                {t('instructor_library.quiz')}
              </h1>
              <div>
                <ul className="mt-3 flex flex-col gap-y-3 marker:text-on-surface">
                  {preQuizQuestions.map((question) => (
                    <Question question={question} key={question.id} />
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="mt-3 rounded-[20px] border border-outline-light px-8 py-9">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-headline-medium text-on-surface">
                  {t('instructor_library.main_experience')}
                </h1>
                <p className="mt-1 text-body-large text-on-surface-var">
                  {t('instructor_library.main_experience_subtitle')}
                </p>
              </div>
              <div>
                <BreakoutButton
                  size="large"
                  kind="secondary"
                  icon={<EyeOpen size={14} />}
                  onClick={() => {
                    showDialog(() => (
                      <ShowExperienceDialog slideDeckId={cubit.slideDeckId} />
                    ))
                  }}
                >
                  {t('instructor_library.preview_experience')}
                </BreakoutButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

const RoleLimitLabel = observer(function RoleLimitLabel({
  assignmentRoles,
  className = 'text-body-medium text-on-surface-var',
  suffix,
}: {
  assignmentRoles: string[] | undefined
  className?: string
  suffix?: string
}) {
  const { t } = useTranslationTyped()
  const cubit = useInstructorSlideDeckCubit()
  if (!assignmentRoles?.length) return null
  const roleNames = assignmentRoles
    .map((id) => cubit.roles.models.find((r) => r.id === id)?.data.roleName)
    .filter(Boolean)
    .join(', ')
  if (!roleNames) return null
  return (
    <span className={className}>
      {t('instructor_library.limited_to_role', { roleName: roleNames })}
      {suffix}
    </span>
  )
})

const Question = observer(function Question({
  question,
}: {
  question: SlideQuestion
}) {
  const { tt } = useTranslationTyped()
  const [isExpanded, setIsExpanded] = useState(false)
  const answersId = `quiz-answers-${question.id}`
  const questionTextId = `quiz-question-${question.id}`

  return (
    <li key={question.id} className="text-label-large">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={answersId}
        className="flex w-full cursor-pointer items-center justify-between text-left"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span id={questionTextId} className="mr-1">
          {question.data.question}
        </span>
        <span className="shrink-0">
          <ChevronLeft
            size={20}
            className={`transition-all ${isExpanded ? 'rotate-90' : 'rotate-270'}`}
          />
        </span>
      </button>
      {isExpanded && (
        // role="group" + aria-labelledby associates the answers with their
        // question for screen readers. The role lives on this wrapping div (not
        // the <ul>/<li>) so the list keeps its native listitem semantics and
        // each answer is announced individually.
        <div role="group" aria-labelledby={questionTextId}>
          <ul id={answersId} className="mt-1 text-body-medium">
            {question.data.answers.map((answer, index) => {
              const isCorrect = question.data.correct === index
              return (
                <li
                  key={index}
                  // list-none (not list-decimal): the CSS-generated ::marker is
                  // exposed by VoiceOver as a separate "AXListMarker" stop.
                  className="relative ml-5 list-none text-body-large"
                >
                  {/*
                    opacity-0 + absolute instead of sr-only: sr-only uses
                    clip:rect(0,0,0,0) and 1×1px which collapses the AX
                    bounding rect so VoiceOver has no anchor for its cursor.
                    opacity-0 is visually invisible but preserves the span's
                    natural dimensions in the accessibility tree, giving
                    VoiceOver a real AXFrame to highlight against.
                  */}
                  <span className="pointer-events-none absolute overflow-hidden whitespace-nowrap opacity-0">
                    {isCorrect
                      ? `${answer} ${tt.instructor_library.quiz_correct_answer()}`
                      : answer}
                  </span>
                  <span aria-hidden="true">
                    {index + 1}. {answer} {isCorrect && '✅'}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </li>
  )
})

async function downloadMaterials(
  fileName: string,
  materials: SlideDeckMaterial[],
  token: Promise<string>
) {
  const blobPromises = materials.map(async (material) => {
    // this can only happen if the URL is malformed
    if (!material.currentMaterialLinkExtension) return null

    const safeFileName = material.safeFileName(
      material.currentMaterialLinkExtension
    )

    if (!safeFileName) return null
    // Signed URLs (the v2 read) carry auth in the signature; an Authorization
    // header changes the CORS preflight and the bucket rejects it.
    const headers: HeadersInit = isSignedUrl(material.data.materialLink)
      ? {}
      : { Authorization: `Bearer ${await token}` }
    const res = await fetch(material.data.materialLink, {
      method: 'GET',
      headers,
    })
    if (!res.ok) return null
    const blob = await res.blob()
    return { blob, name: safeFileName }
  })

  // load jszip and all blobs in parallel
  const [JsZip, ...blobsUnsafe] = await Promise.all([
    (async () => (await import('jszip')).default)(),
    ...blobPromises,
  ])

  const blobs = blobsUnsafe.filter((blob) => !!blob) as {
    name: string
    blob: Blob
  }[]

  if (!blobs.length) {
    toast.error('No materials to download')
    return
  }

  const zipBlob = await (async () => {
    const zip = new JsZip()
    blobs.forEach(({ name, blob }) => {
      zip.file(name, blob)
    })
    return zip.generateAsync({ type: 'blob' })
  })()

  if (!zipBlob) {
    toast.error('Failed to create zip file')
    return
  }

  // have user download the zip file
  const url = URL.createObjectURL(zipBlob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${fileName}.zip`
  a.click()
  URL.revokeObjectURL(url)
  return
}

type ReferencePresentation = {
  dividerClassName: string
  label: string
}

function getReferencePresentation(
  referenceType: SlideDeckReferenceType,
  tt: ReturnType<typeof useTranslationTyped>['tt']
): ReferencePresentation {
  switch (referenceType) {
    case SlideDeckReferenceType.alternativeTo:
      return {
        dividerClassName: 'divide-fixed-accent-color',
        label: tt.instructor_library.alternative_to(),
      }
    case SlideDeckReferenceType.basedOn:
      return {
        dividerClassName: 'divide-on-surface-disabled',
        label: tt.instructor_library.based_on(),
      }
    case SlideDeckReferenceType.supplementTo:
      return {
        dividerClassName: 'divide-on-surface-disabled',
        label: tt.instructor_library.supplement_to(),
      }
    default:
      return {
        dividerClassName: 'divide-fixed-accent-color',
        label: tt.instructor_library.alternative_to(),
      }
  }
}
